/**
 * 計算フィールド（#3145）の設定モーダルへ渡すコンテキストを作るユーティリティ。
 *
 * パターン編集（ItemListManager）と結果画面（PatternAiOcrResult）の両方から
 * 同じ参照スコープで項目候補を出すため、共通化している。
 *
 * 参照スコープ（設計書 §4.4）:
 * - 単一項目 … 他の単一項目のみ参照できる
 * - テーブルの子項目 … 同じテーブルの他の子項目と、単一項目を参照できる
 */
import { type ItemChoiceType, type ItemListType } from '../../types/common';
import { isChoiceDataType } from '../../constants/itemValidation';

/** 計算式の項目挿入プルダウンに出す候補 */
export type CalculationReferenceItem = {
  fieldCode: string;
  itemName: string;
  /** 選択肢項目の選択肢（#3403）。表示名で比較している式の検証に使う */
  choices?: ItemChoiceType[];
};

export type CalculationEditContextValue = {
  /** この項目の式から参照できる項目（参照スコープ適用済み・自分自身は除く） */
  referenceableItems: CalculationReferenceItem[];
  /**
   * SUM で集計できる明細の列（#3358）。
   *
   * 単一項目からは明細の子項目を直接참照できない（どの行か決まらない）が、
   * SUM の中でだけは「全行の集計」として使える。
   * 通常の参照候補と混ぜると行内参照できるように見えるため分けて持つ。
   */
  aggregatableItems: CalculationReferenceItem[];
  /** パターン内の全フィールドコード（スコープ違反の判定用） */
  allFieldCodes: string[];
};

/** 編集対象の項目を item_list 上の位置で示す */
export type CalculationEditTarget = {
  isChild: boolean;
  /** item_list 内での親項目のインデックス */
  parentIndex: number;
  /** 親の children 内でのインデックス（isChild のときのみ使う） */
  childIndex?: number;
};

type Entry = {
  fieldCode: string;
  itemName: string;
  scope: 'single' | 'child';
  parentIndex: number;
  childIndex?: number;
  choices?: ItemChoiceType[];
};

/** 選択肢項目なら選択肢を返す。型を変えた後に残った choices は拾わない */
const pickChoices = (
  type: string | undefined,
  choices: ItemChoiceType[] | undefined,
): ItemChoiceType[] | undefined => (isChoiceDataType(type) ? choices : undefined);

export const buildCalculationEditContext = (
  list: ItemListType[],
  target: CalculationEditTarget,
): CalculationEditContextValue => {
  const entries: Entry[] = [];
  list.forEach((item, parentIndex) => {
    if (item.is_active === false) return;
    if (item.children && item.children.length > 0) {
      item.children.forEach((child, childIndex) => {
        if (child.is_active === false) return;
        entries.push({
          fieldCode: child.field_code ?? '',
          itemName: child.childValue,
          scope: 'child',
          parentIndex,
          childIndex,
          choices: pickChoices(child.type, child.choices),
        });
      });
    } else {
      entries.push({
        fieldCode: item.field_code ?? '',
        itemName: item.parentValue,
        scope: 'single',
        parentIndex,
        choices: pickChoices(item.type, item.choices),
      });
    }
  });

  const isSelf = (entry: Entry): boolean =>
    target.isChild
      ? entry.scope === 'child' &&
        entry.parentIndex === target.parentIndex &&
        entry.childIndex === target.childIndex
      : entry.scope === 'single' && entry.parentIndex === target.parentIndex;

  const referenceableItems = entries
    .filter(entry => !isSelf(entry) && entry.fieldCode !== '')
    .filter(entry =>
      target.isChild
        ? entry.scope === 'single' || entry.parentIndex === target.parentIndex
        : entry.scope === 'single',
    )
    .map(entry => ({
      fieldCode: entry.fieldCode,
      itemName: entry.itemName,
      ...(entry.choices ? { choices: entry.choices } : {}),
    }));

  /**
   * SUM で集計できる明細の列（#3358）。単一項目を編集しているときだけ出す。
   * 明細の列を編集中に自分の明細を SUM すると、全行同じ値になって意味が薄いため
   */
  const aggregatableItems = target.isChild
    ? []
    : entries
        .filter(entry => entry.scope === 'child' && entry.fieldCode !== '')
        .map(entry => ({ fieldCode: entry.fieldCode, itemName: entry.itemName }));

  return {
    referenceableItems,
    aggregatableItems,
    allFieldCodes: entries.map(entry => entry.fieldCode).filter(code => code !== ''),
  };
};
